/**
 * BulletinTab — Broker Bulletin compose + manage screen.
 * Broker can create, pin, and track agent acknowledgments.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faThumbtack, faTrash, faCheck, faUsers, faClock,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../../helpers/api';
import { colors, fonts } from '../shared/colors';

interface Bulletin {
  id: string;
  title: string;
  body: string;
  category: string;
  author_name: string;
  is_pinned: number;
  published_at: string;
}

interface Ack {
  agent_id: string;
  agent_name: string;
  acknowledged_at: string;
}

const CATEGORIES = [
  { value: 'general', label: 'General Update', icon: '📢' },
  { value: 'week', label: 'This Week at the Brokerage (pinned gold digest)', icon: '⭐' },
  { value: 'welcome', label: 'Welcome', icon: '👋' },
  { value: 'market_update', label: 'Market Update', icon: '📊' },
  { value: 'policy', label: 'Policy', icon: '📋' },
  { value: 'training', label: 'Training', icon: '🎓' },
  { value: 'event', label: 'Event (agents can RSVP)', icon: '🗓️' },
  { value: 'win', label: 'Win / Congrats 🎉', icon: '🎉' },
];

interface ManagedDoc {
  id: string;
  filename: string;
  document_type: string | null;
  description: string | null;
  upload_date: string;
  published: boolean;
  is_mine: boolean;
  category: string;
}

const DOC_CATEGORIES = ['Transaction Forms', 'Disclosures', 'Policies', 'Guides & Training', 'Recruiting', 'General'];

interface BulletinTabProps {
  brokerageId?: string;
}

const BulletinTab: React.FC<BulletinTabProps> = ({ brokerageId }) => {
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [acks, setAcks] = useState<Ack[]>([]);
  const [acksLoading, setAcksLoading] = useState(false);

  // Compose form
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [pinned, setPinned] = useState(false);
  const [posting, setPosting] = useState(false);

  // Brokerage documents (publish-to-agents)
  const [docs, setDocs] = useState<ManagedDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docBusy, setDocBusy] = useState<string | null>(null);

  const fetchBulletins = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/agents/broker-bulletins');
      setBulletins(res.data?.bulletins || []);
    } catch {
      setBulletins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/agents/brokerage-documents/manage');
      setDocs(res.data?.documents || []);
    } catch {
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => { fetchBulletins(); fetchDocs(); }, [fetchBulletins, fetchDocs]);

  const handlePublishToggle = async (doc: ManagedDoc, category?: string) => {
    setDocBusy(doc.id);
    try {
      await axiosInstance.post(`/agents/brokerage-documents/${doc.id}/publish`, {
        publish: category !== undefined ? doc.published : !doc.published,
        category: category ?? doc.category,
      });
      fetchDocs();
    } catch (err) {
      console.error('Failed to update document publish state:', err);
    } finally {
      setDocBusy(null);
    }
  };

  const fetchAcks = async (bulletinId: string) => {
    setSelectedId(bulletinId);
    setAcksLoading(true);
    try {
      const res = await axiosInstance.get(`/agents/broker-bulletins/${bulletinId}/acknowledgments`);
      setAcks(res.data?.acknowledgments || []);
    } catch {
      setAcks([]);
    } finally {
      setAcksLoading(false);
    }
  };

  const handlePost = async () => {
    if (!title.trim() || !body.trim()) return;
    setPosting(true);
    try {
      await axiosInstance.post('/agents/broker-bulletins', {
        title, body, category, is_pinned: pinned ? 1 : 0,
        brokerage_id: brokerageId,
      });
      setTitle('');
      setBody('');
      setCategory('general');
      setPinned(false);
      setShowCompose(false);
      fetchBulletins();
    } catch (err) {
      console.error('Failed to post bulletin:', err);
    } finally {
      setPosting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: '#f8f9fa', border: '1px solid #dee2e6',
    borderRadius: '8px', padding: '12px 14px', fontSize: '13px',
    fontFamily: fonts.body, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: colors.navy, margin: 0 }}>Broker Bulletin</h2>
          <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 0' }}>Communicate with your agents — track acknowledgments</p>
        </div>
        <button
          onClick={() => setShowCompose(!showCompose)}
          style={{
            background: showCompose ? '#e74c3c' : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`,
            color: showCompose ? '#fff' : colors.navy,
            border: 'none', borderRadius: '8px', padding: '10px 20px',
            fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FontAwesomeIcon icon={showCompose ? faTrash : faPlus} />
          {showCompose ? 'Cancel' : 'New Bulletin'}
        </button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <div style={{
          background: '#fff', border: `1px solid #dee2e6`, borderLeft: `4px solid ${colors.gold}`,
          borderRadius: '10px', padding: '20px', marginBottom: '24px',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: '16px' }}>
            Compose Bulletin
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Welcome to Chat.RealEstate!" style={inputStyle} />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Message (Markdown supported)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message to agents..." rows={6} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div style={{ paddingTop: '18px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: colors.navy }}>
                <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                <FontAwesomeIcon icon={faThumbtack} style={{ color: colors.gold }} /> Pin to top
              </label>
            </div>
          </div>

          <button
            onClick={handlePost}
            disabled={posting || !title.trim() || !body.trim()}
            style={{
              background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`,
              color: colors.navy, border: 'none', borderRadius: '8px',
              padding: '12px 24px', fontSize: '13px', fontWeight: 700,
              cursor: posting ? 'not-allowed' : 'pointer', opacity: posting ? 0.6 : 1,
            }}
          >
            {posting ? 'Posting...' : 'Post Bulletin'}
          </button>
        </div>
      )}

      {/* Bulletins List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
      ) : bulletins.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '12px', border: '1px solid #eee' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>📭</p>
          <p style={{ color: '#888', fontSize: '14px' }}>No bulletins yet. Click "New Bulletin" to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {bulletins.map(b => {
            const cat = CATEGORIES.find(c => c.value === b.category) || CATEGORIES[0];
            const isSelected = selectedId === b.id;
            const date = new Date(b.published_at);

            return (
              <div key={b.id} style={{
                background: '#fff', border: isSelected ? `2px solid ${colors.gold}` : '1px solid #eee',
                borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
              }} onClick={() => fetchAcks(b.id)}>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: colors.navy }}>
                        {cat.icon} {cat.label}
                      </span>
                      {b.is_pinned ? <span style={{ fontSize: '10px', color: colors.gold, fontWeight: 700 }}>📌 PINNED</span> : null}
                    </div>
                    <span style={{ fontSize: '11px', color: '#aaa' }}>
                      <FontAwesomeIcon icon={faClock} style={{ marginRight: '4px' }} />
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: colors.navy, margin: '0 0 4px' }}>{b.title}</h3>
                  <p style={{ fontSize: '13px', color: '#666', margin: 0, lineHeight: 1.5 }}>
                    {b.body.length > 150 ? b.body.substring(0, 150) + '...' : b.body}
                  </p>
                </div>

                {/* Acknowledgment panel */}
                {isSelected && (
                  <div style={{ background: '#f8f9fa', borderTop: '1px solid #eee', padding: '14px 20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: '10px' }}>
                      <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px' }} />
                      Agent Acknowledgments
                    </div>
                    {acksLoading ? (
                      <p style={{ fontSize: '12px', color: '#888' }}>Loading...</p>
                    ) : acks.length === 0 ? (
                      <p style={{ fontSize: '12px', color: '#888' }}>No agents have acknowledged yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {acks.map(a => (
                          <div key={a.agent_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                            <span style={{ color: colors.navy, fontWeight: 600 }}>
                              <FontAwesomeIcon icon={faCheck} style={{ color: '#27ae60', marginRight: '6px' }} />
                              {a.agent_name || a.agent_id}
                            </span>
                            <span style={{ color: '#aaa' }}>
                              {new Date(a.acknowledged_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Brokerage Documents — publish to agents ── */}
      <div style={{ marginTop: '36px' }}>
        <div style={{ marginBottom: '14px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: colors.navy, margin: 0 }}>Brokerage Documents</h2>
          <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 0' }}>
            Publish documents to every agent's Broker Bulletin page. Upload files in your Documents area first — then publish them here with a category.
          </p>
        </div>

        {docsLoading ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#888', fontSize: '13px' }}>Loading documents...</div>
        ) : docs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px', background: '#fff', borderRadius: '10px', border: '1px solid #eee', color: '#888', fontSize: '13px' }}>
            No documents found. Upload a document in your Documents area, then publish it here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {docs.map(d => (
              <div key={d.id} style={{
                background: '#fff', borderRadius: '10px', padding: '12px 16px',
                border: d.published ? `1px solid ${colors.gold}` : '1px solid #eee',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.filename}
                    {d.published && (
                      <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, color: colors.gold }}>● VISIBLE TO AGENTS</span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: '#aaa' }}>
                    {new Date(d.upload_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {d.description ? ` · ${d.description.length > 70 ? d.description.substring(0, 70) + '…' : d.description}` : ''}
                  </div>
                </div>
                <select
                  value={DOC_CATEGORIES.includes(d.category) ? d.category : 'General'}
                  disabled={!d.is_mine || docBusy === d.id}
                  onChange={e => handlePublishToggle(d, e.target.value)}
                  style={{
                    background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px',
                    padding: '6px 8px', fontSize: '11px', fontFamily: fonts.body, cursor: 'pointer',
                  }}
                >
                  {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  onClick={() => handlePublishToggle(d)}
                  disabled={!d.is_mine || docBusy === d.id}
                  title={d.is_mine ? '' : 'Only the uploader can change publish state'}
                  style={{
                    border: 'none', borderRadius: '8px', padding: '8px 14px',
                    fontSize: '11px', fontWeight: 700, cursor: d.is_mine ? 'pointer' : 'not-allowed',
                    opacity: docBusy === d.id ? 0.5 : 1,
                    background: d.published ? '#f1f3f5' : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`,
                    color: d.published ? '#666' : colors.navy,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {docBusy === d.id ? '...' : d.published ? 'Unpublish' : 'Publish to Agents'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulletinTab;
